import { styleConstants, themeConstants } from '@paywithextend/components'
import ExtButton from 'ext-ui/components/Button/Button'
import { spacing } from 'ext-ui/style/spacing'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import Box from 'src/components/Box/Box'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import ButtonAndMoreDropdown from 'src/components/ButtonAndMoreDropdown/ButtonAndMoreDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { MoreDropdownDesigns } from 'src/components/MoreDropdown/MoreDropdown'
import SvgCancelIcon from 'src/components/Svgs/SvgCancelIcon'
import SvgChevronDownLoD from 'src/components/Svgs/SvgChevronDownLoD'
import SvgChevronUpLoD from 'src/components/Svgs/SvgChevronUpLoD'
import SvgExternalLink from 'src/components/Svgs/SvgExternalLink'
import SvgPencilIcon from 'src/components/Svgs/SvgPencilIcon'
import SvgRecurring from 'src/components/Svgs/SvgRecurring'
import { useTranslate } from 'src/localization/Trns'
import {
  BillingCycle,
  getSaasTierLabel,
  SaasTierType,
  SubscriptionStatus,
  Tier,
} from 'src/models/organizations.models'
import { connectedPush } from 'src/navigation/Lnk'
import { CANCEL_SUBSCRIPTION_ROUTE, PRICING_ROUTE } from 'src/navigation/urls'
import {
  useCancelSubscription,
  usePutManageSubscription,
  usePutRenewSubscriptionRequest,
  useQueryOrganizationSaasTier,
  useQueryOrganizationSubscription,
} from 'src/queries/organizations/saas.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { css } from 'styled-components'

import styles from './ManageSubscriptionHeader.style'

const getBillingCycleLabel = (billingCycle: BillingCycle): string => {
  switch (billingCycle) {
    case BillingCycle.MONTHLY:
      return 'Monthly'
    case BillingCycle.QUARTERLY:
      return 'Quarterly'
    case BillingCycle.YEARLY:
      return 'Yearly'
    case BillingCycle.UNKNOWN:
      return 'Unknown'
  }
}

interface ManageSubscriptionHeaderProps {
  organizationId: string
  currentTier: SaasTierType
  onChargebeeOpened?: () => void
}
const ManageSubscriptionHeader: React.FC<ManageSubscriptionHeaderProps> = (
  props,
) => {
  const { organizationId, currentTier, onChargebeeOpened } = props
  const translate = useTranslate()
  const dispatch = useDispatch()
  const { analytics } = useAnalyticsContext()

  const { data: currentPlan } = useQueryOrganizationSaasTier({
    orgId: organizationId,
  })
  const { data: organizationSubscription } = useQueryOrganizationSubscription({
    orgId: organizationId,
  })

  const { mutate: renewSubscription } = usePutRenewSubscriptionRequest()
  const { mutate: putManageSubscription } = usePutManageSubscription()

  const { mutate: cancelSubscription } = useCancelSubscription()
  const { show_dynamic_pricing_page: showDynamicPricingPage } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const subscriptionState = organizationSubscription?.subscription?.state
  const canManageStarterSubscription =
    currentTier === 'STARTER' &&
    organizationSubscription?.subscription !== undefined &&
    organizationSubscription?.subscription !== null
  const canManageSubscription =
    currentTier !== 'STARTER' || canManageStarterSubscription
  const shouldShowManagePlanDropdown =
    canManageSubscription && currentTier !== 'STARTER'
  const canCancelOrRenewSubscription = currentTier !== 'STARTER'
  const nextInvoiceDate =
    subscriptionState?.status === SubscriptionStatus.ACTIVE
      ? moment(subscriptionState.nextBillingAt).format('MMMM D, YYYY')
      : subscriptionState?.status === SubscriptionStatus.IN_TRIAL
        ? moment(subscriptionState.trialEnd).format('MMMM D, YYYY')
        : translate('N/A')

  const handleManageSubscription = (): void => {
    analytics.track('manage subscription', {
      action: 'clicked',
      tier: currentTier,
    })
    putManageSubscription(
      { orgId: organizationId, redirect: false, body: {} },
      {
        onSuccess: ({ url }) => {
          if (url) {
            window.open(url, '_blank', 'noreferrer')
            onChargebeeOpened?.()
          }
        },
      },
    )
  }

  const handleCancelSubscription = (): void => {
    analytics.track('cancel subscription', {
      action: 'started',
      tier: currentTier,
    })
    if (organizationSubscription?.subscription?.tier === Tier.ENTERPRISE) {
      // Downgrading from Enterprise involves customer support
      // No need to show downgrade confirmation modal
      analytics.track('cancel subscription', {
        action: 'confirmed',
        tier: Tier.ENTERPRISE,
      })
      cancelSubscription({
        orgId: organizationId,
        tier: currentPlan?.tier,
        body: {
          intention: 'cancel_subscription',
        },
      })
      return
    }
    // tier is Pro - can auto downgrade
    dispatch(connectedPush(CANCEL_SUBSCRIPTION_ROUTE(organizationId)))
  }

  const handleRenewSubscription = (): void => {
    analytics.track('cancel subscription', {
      action: 'reactivated',
      tier: currentTier,
    })
    renewSubscription(
      { orgId: organizationId, body: {} },
      {
        onSuccess: () => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <span>
                  {translate(
                    'Your subscription has been renewed successfully!',
                  )}
                </span>
              ),
            }),
          )
        },
        onError: () => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <span>
                  {translate(
                    'Oops! There was a problem renewing your subscription. Please try again later or contact customer support.',
                  )}
                </span>
              ),
              failureColorScheme: true,
            }),
          )
        },
      },
    )
  }

  return (
    <div className={'ManageSubscriptionHeader'} css={styles}>
      <div className={'flex-column gap-6'}>
        <div className={'flex-column gap-3'}>
          <span className={'plan-subheader'}>{translate('Current Plan')}</span>
          <span className={'title'}>
            {/*TODO: refactor to move this into feature component so we can have skeleton loader*/}
            {translate('${tier} Plan', {
              tier: getSaasTierLabel(currentPlan?.tier ?? 'STARTER'),
            })}
          </span>
        </div>
        {/* TODO: actual link*/}
        <HoverableLink
          className="flex-row gap-1"
          ariaLabelSource={"What's included in this plan?"}
          href={
            showDynamicPricingPage
              ? 'https://paywithextend.com/pricing'
              : PRICING_ROUTE()
          }
        >
          {translate("What's included in this plan?")}
          <SvgExternalLink width={16} height={16} viewBox={'0 0 24 24'} />
        </HoverableLink>
        {shouldShowManagePlanDropdown && (
          <ButtonAndMoreDropdown
            buttonProps={{
              onClick: handleManageSubscription,
              text: (
                <span
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    color: ${themeConstants.colors.cta};
                  `}
                >
                  {translate('Manage Plan')}
                  <SvgExternalLink
                    width={14}
                    height={14}
                    viewBox={'0 0 24 24'}
                  />
                </span>
              ),
              design: ButtonDesigns.plainNoShadow,
              type: ButtonTypes.button,
            }}
            moreDropdownProps={{
              alignRight: true,
              design: MoreDropdownDesigns.CtaBlueWhiteBg,
              minWidth: 330,
              verticalBuffer: 8,
              topOptions: [
                {
                  id: 'modifyPlan',
                  key: 'modifyPlan',
                  testId: 'modifyPlan',
                  label: (
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        gap: 8px;
                        left: -2px;
                        position: relative;
                        width: 100%;
                        font-size: 14px;
                        font-family: ${styleConstants.fontFamilies.regular};
                        color: ${themeConstants.colors.cta};
                      `}
                    >
                      <SvgPencilIcon />
                      {translate('Modify Plan')}
                      <SvgExternalLink
                        width={14}
                        height={14}
                        viewBox={'0 0 24 24'}
                      />
                    </div>
                  ),
                  ariaLabelSource: 'Modify Plan',
                  onClick: handleManageSubscription,
                },
              ],
              bottomOptions: [
                ...(canCancelOrRenewSubscription &&
                organizationSubscription?.subscription?.state.status ===
                  SubscriptionStatus.ACTIVE
                  ? [
                      {
                        id: 'cancelSubscription',
                        testId: 'cancelSubscription',
                        key: 'cancelSubscription',
                        label: (
                          <div
                            css={css`
                              display: flex;
                              flex-direction: row;
                              align-items: center;
                              gap: 8px;
                              left: -2px;
                              position: relative;
                              width: 100%;
                              font-size: 14px;
                              font-family: ${styleConstants.fontFamilies
                                .regular};
                              color: ${styleConstants.colors.red_dark};
                            `}
                          >
                            <SvgCancelIcon />
                            {translate('Downgrade Subscription')}
                          </div>
                        ),
                        ariaLabelSource: 'Downgrade Subscription',
                        onClick: handleCancelSubscription,
                      },
                    ]
                  : []),
                ...(canCancelOrRenewSubscription &&
                organizationSubscription?.subscription?.state.status ===
                  SubscriptionStatus.NON_RENEWING
                  ? [
                      {
                        id: 'renewSubscription',
                        testId: 'renewSubscription',
                        key: 'renewSubscription',
                        label: (
                          <div
                            css={css`
                              display: flex;
                              flex-direction: row;
                              align-items: center;
                              gap: 8px;
                              left: -2px;
                              position: relative;
                              width: 100%;
                              font-size: 14px;
                              font-family: ${styleConstants.fontFamilies
                                .regular};
                              color: ${themeConstants.colors.cta};
                            `}
                          >
                            <SvgRecurring height={16} width={16} />
                            {translate('Reactivate Subscription')}
                          </div>
                        ),
                        ariaLabelSource: 'Reactivate Subscription',
                        onClick: handleRenewSubscription,
                      },
                    ]
                  : []),
              ],
              openComponent: (
                <span
                  css={css`
                    color: ${themeConstants.colors.cta};
                  `}
                >
                  <SvgChevronUpLoD />
                </span>
              ),
              closedComponent: (
                <span
                  css={css`
                    color: ${themeConstants.colors.cta};
                  `}
                >
                  <SvgChevronDownLoD />
                </span>
              ),
            }}
          />
        )}
      </div>

      {currentTier !== 'STARTER' &&
        canManageSubscription &&
        organizationSubscription?.subscription && (
          <Box padding={spacing(5)} outlineBorder noShadow>
            <div className={'flex-column gap-5'}>
              <div className={'flex-row gap-5 justify-between align-center'}>
                <span className={'font-h3'}>
                  {translate('${recurrence} Invoice', {
                    recurrence: getBillingCycleLabel(
                      organizationSubscription.subscription.billingCycle,
                    ),
                  })}
                </span>
                <ExtButton
                  variant={'toolbar'}
                  dropShadow={false}
                  onClick={handleManageSubscription}
                >
                  <span
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 4px;
                    `}
                  >
                    {translate('View')}
                    <SvgExternalLink
                      width={14}
                      height={14}
                      viewBox={'0 0 24 24'}
                    />
                  </span>
                </ExtButton>
              </div>
              <div className={'divider'} />
              <div className={'flex-column'}>
                <div className={'details-row'}>
                  <span className={'font-md text-secondary'}>
                    {translate('Next Invoice Date')}
                  </span>
                  <span className={'font-md'}>{nextInvoiceDate}</span>
                </div>
              </div>
            </div>
          </Box>
        )}
    </div>
  )
}

export default ManageSubscriptionHeader
